import type { OverwriteMinAmountMessageConfig } from '../interfaces/config'
import type { RootState } from '../redux/store'
import { isHidePricesEnabled } from './hidePrices'
import flatTexts from './flatTexts'

const PERCENTAGE_PLACEHOLDER = /\{\{\s*percentage\s*\}\}/g

export const getMinAmountProgress = (
  total: number,
  minAmount: number,
): number => {
  if (!minAmount || minAmount <= 0) {
    return 100
  }
  return Math.min(100, Math.max(0, Math.round((total * 100) / minAmount)))
}

interface ResolveMinAmountProgressMessageArgs {
  config: OverwriteMinAmountMessageConfig | undefined
  template: string | undefined
  hidePricesActive: boolean
  total: number
  minAmount: number
}

export const resolveMinAmountProgressMessage = ({
  config,
  template,
  hidePricesActive,
  total,
  minAmount,
}: ResolveMinAmountProgressMessageArgs): string | undefined => {
  if (!config?.enabled) {
    return undefined
  }
  const audience = config.audience ?? 'all'
  if (audience === 'hidePrices' && !hidePricesActive) {
    return undefined
  }
  if (!Number.isFinite(total) || !(minAmount > 0)) {
    return undefined
  }
  const source = config.message || template
  if (typeof source !== 'string') {
    return undefined
  }
  const message = source.replace(
    PERCENTAGE_PLACEHOLDER,
    String(getMinAmountProgress(total, minAmount)),
  )
  return message === source ? undefined : message
}

export const getMinAmountProgressMessage = (
  state: RootState,
  resolved: Record<string, unknown>,
): string | undefined => {
  const minAmount = resolved.minAmount as { value?: unknown } | undefined
  return resolveMinAmountProgressMessage({
    config: state?.defaultSlice?.config?.options?.overwriteMinAmountMessage,
    template: flatTexts(state?.defaultSlice?.config?.texts?.cart).minAmountProgress,
    hidePricesActive: isHidePricesEnabled(state),
    total: Number(resolved.total),
    minAmount: Number(minAmount?.value),
  })
}
